/**
 * Rachunek „Sprawdź ofertę”: typowy zakres ceny instalacji złożony z pozycji.
 *
 * Czyste funkcje, bez Reacta, żeby dało się je przetestować i żeby raport
 * i pomiar liczyły to samo. Dane o sprzęcie przychodzą z backendu
 * (`POST /api/sprawdz_oferte/`), stawki z `config/cennikOferty.js`.
 *
 * ═══ ZASADY ═══
 * - Wszystko liczymy w NETTO. Brutto to netto z VAT oferty, tak jak na fakturze
 *   instalatora; porównanie z ofertą też idzie w netto.
 * - Pozycja bez ceny nie udaje zera: jest oznaczona i nie wchodzi do sumy,
 *   a szacunek dostaje flagę `niepelny`.
 * - „Nie wiem” przy dodatku podnosi tylko górną granicę: może go być, może nie.
 */
import { kosztKonstrukcji, MOC_ODNIESIENIA_W } from './konstrukcja'
import { kosztMontazu } from '../config/cennikMontazu'
import {
  VAT_SKLEPOW, ROZRZUT_KONSTRUKCJI, ROZRZUT_MONTAZU, ZASILANIE_AWARYJNE_NETTO,
  MODUL_ZASILANIA_AWARYJNEGO_NETTO, OPTYMALIZATOR_NETTO_SZT, KOSZTY_FIRMY, DACHY,
  MAGAZYN_ZL_ZA_KWH_BRUTTO, MODUL_ZL_ZA_W_BRUTTO, FALOWNIK_ZL_ZA_KW_BRUTTO, POZYCJE_ROBOCIZNY,
  montazMagazynu, obslugaSprzedazy,
} from '../config/cennikOferty'

export const naNetto = (brutto, vat = VAT_SKLEPOW) => Number(brutto) / (1 + vat / 100)
export const naBrutto = (netto, vat) => Number(netto) * (1 + vat / 100)

/** Kwota, którą wpisał użytkownik, sprowadzona do netto. */
export const cenaOfertyNetto = ({ kwota, tryb, vat }) => {
  const k = Number(kwota)
  if (!Number.isFinite(k) || k <= 0) return null
  return tryb === 'netto' ? k : naNetto(k, vat)
}

/** Konstrukcja dla grupy dachu: średnia z jej połaci, netto. */
export const konstrukcjaDachu = ({ dach, kwp, mocModuluW }) => {
  const grupa = DACHY.find((d) => d.key === dach) || DACHY[0]
  if (!kwp) return null
  const kwoty = grupa.polacie.map((p) => kosztKonstrukcji({
    kwp, konstrukcja: p, mocModuluW: mocModuluW || MOC_ODNIESIENIA_W,
  }).razemZl)
  return kwoty.reduce((a, b) => a + b, 0) / kwoty.length
}

const zakres = (od, doK) => ({ od, do: doK })
const zRozrzutem = (kwota, r) => zakres(kwota * (1 - r), kwota * (1 + r))

/** Dodatek z odpowiedzią Tak / Nie / Nie wiem: „?” podnosi tylko górną granicę. */
const dodatek = (odpowiedz, kwota) => {
  if (odpowiedz === 'tak') return zakres(kwota, kwota)
  if (odpowiedz === '?') return zakres(0, kwota)
  return null
}

/**
 * Pozycje szacunku. Każda: { klucz, nazwa, od, do, zrodlo, brakCeny? }.
 * `wynik` to odpowiedź backendu, `wejscie` to odpowiedzi z formularza.
 */
export const pozycjeSzacunku = (wynik, wejscie) => {
  const { liczbaModulow, dach, zasilanieAwaryjne, optymalizatory } = wejscie
  // Magazyn bywa w ofercie w kilku sztukach („2 × SigenStor BAT 10.0”); cena z katalogu jest za sztukę.
  const sztukMagazynu = wejscie.liczbaMagazynu > 1 ? wejscie.liczbaMagazynu : 1
  const mocModuluW = wynik?.moduly?.moc_w || null
  const kwp = wynik?.moduly?.kwp || (mocModuluW && liczbaModulow ? (liczbaModulow * mocModuluW) / 1000 : null)
  const p = []

  // `szacunek`: { od, do, zrodlo } w netto za całą pozycję, gdy katalog nie zna ceny, ale
  // znamy moc (prośba właściciela 2026-10-01: lepiej szeroki szacunek niż „brak ceny”).
  const sprzet = (klucz, nazwa, sekcja, mnoznik = 1, zrodlo, szacunek = null) => {
    if (!sekcja) return
    const od = sekcja.cena_od ?? sekcja.cena_szt_od
    const doK = sekcja.cena_do ?? sekcja.cena_szt_do
    if (od == null || doK == null) {
      if (szacunek) { p.push({ klucz, nazwa, ...szacunek, modele: [], przyblizone: true }); return }
      p.push({ klucz, nazwa, od: null, do: null, zrodlo: 'nie znaleźliśmy ceny w katalogu', brakCeny: true })
      return
    }
    // Modele, z których wzięliśmy ceny: raport linkuje do ich stron.
    const modele = (sekcja.kandydaci || []).filter((k) => k.url)
      .map((k) => ({ nazwa: k.nazwa, url: k.url, ofert: k.ofert, img_url: k.img_url || null }))
    p.push({ klucz, nazwa, od: naNetto(od) * mnoznik, do: naNetto(doK) * mnoznik, zrodlo, modele,
      przyblizone: Boolean(sekcja.wielkosc_przyblizona || sekcja.moc_przyblizona) })
  }

  const ileKandydatow = (s) => (s?.kandydaci?.length ?? 0)
  const liczbaPl = (v) => String(v).replace('.', ',')
  // Producent bez tej wielkości w katalogu: cena z jego najbliższych modeli, przeliczona.
  const przyblizona = (s, jedn) => `brak tej ${jedn === 'kWh' ? 'pojemności' : 'mocy'} w katalogu: najbliższe modele tej marki, `
    + `cena przeliczona na ${liczbaPl(s.wielkosc_z_oferty)} ${jedn}`
  // Falownik bez ceny: moc z formularza, a bez niej moc modułów (falownik dobiera się do niej).
  const kwFalownika = Number(wejscie.falownik?.model?.moc ?? wejscie.falownik?.moc) || kwp
  const zMagazynem = wejscie.magazyn && wejscie.magazyn.brak === false
  const stawkaFalownika = zMagazynem ? FALOWNIK_ZL_ZA_KW_BRUTTO.hybrydowy : FALOWNIK_ZL_ZA_KW_BRUTTO.dowolny
  const szacunekFalownika = kwFalownika > 0 ? {
    od: naNetto(stawkaFalownika.od * kwFalownika), do: naNetto(stawkaFalownika.do * kwFalownika),
    zrodlo: `brak ceny tego modelu: szacunek z mocy ${liczbaPl(Math.round(kwFalownika * 100) / 100)} kW, typowo `
      + `${stawkaFalownika.od}–${stawkaFalownika.do} zł za kW${zMagazynem ? ' (falownik hybrydowy)' : ''} w sklepach`,
  } : null
  // Rozbudowa o magazyn bez wymiany falownika: zostaje obecny, więc nie ma go w rachunku.
  const bezNowegoFalownika = wejscie.rozbudowa && !wejscie.falownik
  if (!bezNowegoFalownika) sprzet('falownik', 'Falownik', wynik?.falownik, 1,
    wynik?.falownik?.wielkosc_przyblizona ? przyblizona(wynik.falownik, 'kW')
      : ileKandydatow(wynik?.falownik) > 1
        ? `ceny ${ileKandydatow(wynik.falownik)} pasujących modeli`
        : 'ceny modelu w sklepach', szacunekFalownika)
  // Magazyn bez ceny w katalogu, ale ze znaną pojemnością: szacunek z typowej ceny za kWh.
  const kwhMagazynu = Number(wejscie.magazyn?.model?.moc ?? wejscie.magazyn?.moc)
  const bezCenyMagazynu = wynik?.magazyn && (wynik.magazyn.cena_od == null || wynik.magazyn.cena_do == null)
  if (bezCenyMagazynu && kwhMagazynu > 0) {
    const kwh = String(Math.round(kwhMagazynu * 100) / 100).replace('.', ',')
    p.push({
      klucz: 'magazyn', nazwa: sztukMagazynu > 1 ? `Magazyn energii × ${sztukMagazynu}` : 'Magazyn energii',
      od: naNetto(MAGAZYN_ZL_ZA_KWH_BRUTTO.od * kwhMagazynu) * sztukMagazynu,
      do: naNetto(MAGAZYN_ZL_ZA_KWH_BRUTTO.do * kwhMagazynu) * sztukMagazynu,
      zrodlo: `brak ceny tego modelu: szacunek z pojemności ${kwh} kWh, typowo ${MAGAZYN_ZL_ZA_KWH_BRUTTO.od}–${MAGAZYN_ZL_ZA_KWH_BRUTTO.do} zł za kWh w sklepach`
        + (sztukMagazynu > 1 ? `, razy ${sztukMagazynu} szt.` : ''),
      modele: [], przyblizone: true,
    })
  } else if (wynik?.magazyn) {
    sprzet('magazyn', sztukMagazynu > 1 ? `Magazyn energii × ${sztukMagazynu}` : 'Magazyn energii', wynik.magazyn, sztukMagazynu,
      (wynik.magazyn.wielkosc_przyblizona ? przyblizona(wynik.magazyn, 'kWh')
        : ileKandydatow(wynik.magazyn) > 1
          ? `ceny ${ileKandydatow(wynik.magazyn)} pasujących modeli`
          : 'ceny modelu w sklepach') + (sztukMagazynu > 1 ? `, razy ${sztukMagazynu} szt.` : ''))
  }
  if (liczbaModulow) {
    const wModulu = mocModuluW || Number(wejscie.moduly?.model?.moc ?? wejscie.moduly?.moc) || null
    const szacunekModulow = wModulu ? {
      od: naNetto(MODUL_ZL_ZA_W_BRUTTO.od * wModulu) * liczbaModulow,
      do: naNetto(MODUL_ZL_ZA_W_BRUTTO.do * wModulu) * liczbaModulow,
      zrodlo: `brak ceny tego modelu: szacunek z mocy ${wModulu} W, typowo ${liczbaPl(MODUL_ZL_ZA_W_BRUTTO.od)}–`
        + `${liczbaPl(MODUL_ZL_ZA_W_BRUTTO.do)} zł za wat w sklepach, razy ${liczbaModulow} szt.`,
    } : null
    sprzet('moduly', `Moduły × ${liczbaModulow}`, wynik?.moduly, liczbaModulow,
      wynik?.moduly?.moc_przyblizona
        ? `brak tej mocy w katalogu: najbliższe moce tej marki, cena przeliczona na ${mocModuluW} W`
        : 'cena za sztukę razy liczba modułów', szacunekModulow)
  }

  const konstrukcja = konstrukcjaDachu({ dach, kwp, mocModuluW })
  if (konstrukcja != null) {
    const grupa = DACHY.find((d) => d.key === dach) || DACHY[0]
    p.push({ klucz: 'konstrukcja', nazwa: 'Konstrukcja', ...zRozrzutem(konstrukcja, ROZRZUT_KONSTRUKCJI),
      zrodlo: `średnia stawka rynkowa, ${grupa.label.toLowerCase()}` })
  }

  const montaz = kwp ? kosztMontazu(kwp) : null
  if (montaz != null) {
    p.push({ klucz: 'montaz', nazwa: 'Montaż z kablami i zabezpieczeniami', ...zRozrzutem(montaz, ROZRZUT_MONTAZU),
      zrodlo: 'średnia stawka rynkowa' })
  }

  // Rozbudowa istniejącej instalacji o magazyn: zamiast montażu modułów montaż magazynu
  // (i ewentualnej wymiany falownika), stawka według łącznej pojemności (`config/cennikOferty.js`).
  if (wejscie.rozbudowa && zMagazynem) {
    const kwhSztuki = kwhMagazynu > 0 ? kwhMagazynu : Number(wynik?.magazyn?.kandydaci?.[0]?.pojemnosc_kwh) || 0
    const kwhRazem = kwhSztuki * sztukMagazynu
    const stawka = montazMagazynu(kwhRazem)
    if (stawka != null) {
      p.push({ klucz: 'montaz_magazynu', nazwa: 'Montaż magazynu z podłączeniem', od: stawka, do: stawka,
        zrodlo: `stawka za rozbudowę o magazyn ${String(Math.round(kwhRazem * 100) / 100).replace('.', ',')} kWh`
          + `${wejscie.falownik ? ', z wymianą falownika' : ''}` })
    }
  }

  // Zasilanie awaryjne: prace zawsze, osobny moduł tylko gdy falownik go nie ma.
  const potrzebnyModul = wynik?.falownik?.backup === 'nie'
  const zasilanie = dodatek(zasilanieAwaryjne,
    ZASILANIE_AWARYJNE_NETTO + (potrzebnyModul ? MODUL_ZASILANIA_AWARYJNEGO_NETTO : 0))
  if (zasilanie) {
    p.push({ klucz: 'zasilanie', nazwa: 'Zasilanie awaryjne', ...zasilanie,
      zrodlo: potrzebnyModul
        ? 'średnia stawka rynkowa za prace i osobny moduł (falownik go nie ma wbudowanego)'
        : 'średnia stawka rynkowa za prace techniczne' })
  }

  const optym = liczbaModulow ? dodatek(optymalizatory, OPTYMALIZATOR_NETTO_SZT * liczbaModulow) : null
  if (optym) {
    p.push({ klucz: 'optymalizatory', nazwa: 'Optymalizatory', ...optym,
      zrodlo: `mediana cen w sklepach × ${liczbaModulow} szt.` })
  }

  // Procent liczymy od środka pozycji robocizny (decyzja 2026-10-07, `config/cennikOferty.js`).
  // Liczony od „od” i od „do” mnożyłby niepewność pozycji przez niepewność narzutu.
  // Bez żadnej pozycji robocizny (np. sam magazyn bez montażu modułów) zostaje narzut od całości,
  // żeby nie zgubić kosztów firmy zupełnie.
  const robocizna = p.filter((x) => !x.brakCeny && POZYCJE_ROBOCIZNY.includes(x.klucz))
  const baza = robocizna.length ? robocizna : p.filter((x) => !x.brakCeny)
  const srodekBazy = baza.reduce((a, x) => a + (x.od + x.do) / 2, 0)
  if (baza.length) {
    // Do procentu od robocizny dochodzi stały narzut na obsługę sprzedaży (decyzja 2026-10-14,
    // `config/cennikOferty.js`). Tylko przy nowej instalacji ze znaną mocą; przy rozbudowie o sam
    // magazyn go nie ma. Opis pozycji celowo bez procentów i kwot: widełek narzutu nie pokazujemy.
    const sprzedaz = wejscie.rozbudowa ? null : obslugaSprzedazy(kwp)
    p.push({ klucz: 'koszty_firmy', nazwa: 'Obsługa, gwarancja i koszty firmy',
      od: srodekBazy * KOSZTY_FIRMY.od + (sprzedaz?.od || 0),
      do: srodekBazy * KOSZTY_FIRMY.do + (sprzedaz?.do || 0),
      zrodlo: 'projekt, formalności, dojazd, obsługa klienta i sprzedaży, gwarancja i zysk firmy' })
  }
  return p
}

/**
 * Typowy zakres całości (netto) i informacja, czy czegoś zabrakło.
 *
 * NIE sumujemy skrajności: pozycje są od siebie niezależne i rzadko wszystkie naraz są
 * najtańsze albo najdroższe. Środek to suma środków pozycji, a połowa szerokości to
 * pierwiastek z sumy kwadratów połówek (składanie niezależnych niepewności). Zakres jest
 * przez to węższy niż suma kolumny „od” i suma kolumny „do” w tabeli, np. dla oferty
 * 9,9 kWp z magazynem ±8% zamiast ±13% (decyzja właściciela 2026-09-28: „zmniejszyć z głową”).
 */
export const sumaSzacunku = (pozycje) => {
  const z = pozycje.filter((x) => !x.brakCeny)
  const srodek = z.reduce((a, x) => a + (x.od + x.do) / 2, 0)
  const polowa = Math.sqrt(z.reduce((a, x) => a + ((x.do - x.od) / 2) ** 2, 0))
  return { od: srodek - polowa, do: srodek + polowa, niepelny: pozycje.some((x) => x.brakCeny) }
}

/**
 * Gdzie leży oferta wobec typowego zakresu. Bez słowa „za drogo”:
 * `typ` to 'ponizej' | 'w_zakresie' | 'powyzej', `procent` to odległość od granicy.
 */
export const werdyktOferty = ({ ofertaNetto, od, do: doK }) => {
  if (ofertaNetto == null || !od || !doK) return null
  if (ofertaNetto > doK) return { typ: 'powyzej', procent: Math.round((ofertaNetto / doK - 1) * 100) }
  if (ofertaNetto < od) return { typ: 'ponizej', procent: Math.round((1 - ofertaNetto / od) * 100) }
  return { typ: 'w_zakresie', procent: 0 }
}

/**
 * Pytania, które warto zadać przy KAŻDEJ ofercie, niezależnie od sprzętu.
 * Na podstawie checklisty z artykułu „Jak porównać oferty na instalację PV?”
 * (`public/knowledge-base/instalacje-pv/jak-porownac-oferty-na-instalacje-pv.md`).
 * Nie wchodzą do licznika pytań w porównaniu ofert, bo są wspólne dla wszystkich.
 */
export const PYTANIA_ZAWSZE = [
  'Jaką gwarancję dajecie na sam montaż i jaki czas reakcji serwisu zapisujecie w umowie?',
  'Kto wykonuje montaż: własna ekipa czy podwykonawca? Jakie macie uprawnienia (UDT, SEP) i ubezpieczenie OC?',
  'Jaki roczny uzysk zakładacie i na jakiej symulacji go opieracie?',
  'Kto pokrywa robociznę i dojazd przy wymianie gwarancyjnej sprzętu?',
  'Czy zgłoszenie instalacji i magazynu do operatora sieci jest w cenie?',
]

/** Wartość pola marki (dane badawcze z backendu) albo null. */
export const poleMarki = (marka, pole) => marka?.atrybuty?.find((a) => a.pole === pole)?.wartosc || null

/**
 * Serwis marki w Polsce z tekstu danych badawczych: 'tak', 'nie' albo null.
 * Wpisy zaczynają się od odpowiedzi („Tak. …”, „Nie ma osobnej spółki…”, „Brak oficjalnego…”);
 * wszystko inne zostaje bez oceny, żeby nie dopisywać marce braku, którego nie stwierdzono.
 */
export const ocenaSerwisu = (marka) => {
  const t = (poleMarki(marka, 'serwis_pl_czy_jest') || '').trim().toLowerCase()
  if (t.startsWith('tak')) return 'tak'
  if (t.startsWith('nie') || t.startsWith('brak')) return 'nie'
  return null
}

/** Falownik i magazyn tej samej marki: jedna organizacja serwisowa, jedna karta w raporcie. */
export const wspolnaMarka = (wynik) => {
  const f = wynik?.falownik?.marka
  const m = wynik?.magazyn?.marka
  return Boolean(f && m && f.producent === m.producent)
}

/**
 * Serwis magazynu w Polsce. Gdy o magazynie nic nie wiemy, a to ta sama marka co falownik,
 * bierzemy ocenę z falownika: inaczej raport mówiłby „Huawei: jest” i „Huawei: brak danych” naraz.
 */
export const serwisMagazynu = (wynik) => {
  const wlasna = ocenaSerwisu(wynik?.magazyn?.marka)
  if (wlasna || !wspolnaMarka(wynik)) return wlasna
  return ocenaSerwisu(wynik.falownik.marka)
}

/** Pytania o markę: brak polskiej spółki i reklamacje zgłaszane tylko przez instalatora. */
const pytaniaOMarce = (marka, co) => {
  if (!marka) return []
  const q = []
  if (ocenaSerwisu(marka) === 'nie') {
    q.push(`${marka.producent} nie ma w Polsce własnej spółki. Kto przyjmuje reklamację ${co}, gdzie jest naprawiany i ile to trwa?`)
  }
  // Zastrzeżeń bywa kilka (np. FoxESS); każde sprawdzamy osobno, żeby nie sklejać dwóch różnych wpisów.
  const tylkoPrzezFirme = (marka.atrybuty || []).some((a) => a.pole === 'zastrzezenia'
    && /reklamacj/i.test(a.wartosc || '') && /(instalator|od firm)/i.test(a.wartosc || ''))
  if (tylkoPrzezFirme) {
    q.push(`Reklamację ${co} ${marka.producent} zgłasza firma, nie właściciel domu. Czy zgłaszacie usterki w ramach umowy i kto to zrobi, jeśli Wasza firma przestanie działać?`)
  }
  return q
}

/**
 * Pytania do instalatora dotyczące TEJ oferty: z tego, czego nie wiemy albo co wymaga
 * potwierdzenia. Kolejność: od największej niewiadomej. Pytania wspólne dla każdej
 * oferty są osobno w `PYTANIA_ZAWSZE`.
 */
export const pytaniaDoInstalatora = (wynik, wejscie) => {
  const q = []
  // Bez pytań „jaki dokładnie model…”: raport liczy zakres dla pasujących modeli, a lista
  // i tak była długa (decyzja właściciela 2026-09-28).
  const { magazyn, zasilanieAwaryjne, liczbaModulow } = wejscie
  const para = wynik?.zgodnosc?.para
  if (para && para.status !== 'compatible') {
    if (para.status === 'conditional') {
      q.push('Współpraca falownika z magazynem wymaga warunków (np. wersji oprogramowania albo BMS). Czy zostaną spełnione i kto to ustawi?')
    } else {
      q.push('Czy producent falownika lub magazynu potwierdza ich współpracę? Poproś o dokument producenta.')
    }
  }
  if (magazyn && !magazyn.brak) {
    q.push('Ile wynosi pojemność magazynu możliwa do wykorzystania, a ile nominalna?')
  }
  const bezUkladu = (wynik?.stringi || []).some((s) => s.ok && s.liczba_pasuje === false)
  if (bezUkladu) {
    q.push(`Jak zostaną połączone moduły: ile łańcuchów i po ile modułów na każde wejście falownika (${liczbaModulow} szt.)?`)
  }
  if (zasilanieAwaryjne === '?') {
    q.push('Czy instalacja będzie miała zasilanie awaryjne? Jeśli tak: czy włącza się samo, czy trzeba je załączyć ręcznie, i które fazy, pomieszczenia i gniazdka będą zasilane przy braku prądu?')
  } else if (zasilanieAwaryjne === 'tak') {
    // Oferty często obiecują „cały dom”, a dane producenta mówią o wybranych obwodach.
    q.push(/wybrane obwody/i.test(wynik?.falownik?.backup_opis || '')
      ? 'Według producenta ten falownik zasila przy braku prądu wybrane obwody domu. Które obwody obejmie zasilanie awaryjne, czy włącza się samo, czy trzeba je załączyć ręcznie?'
      : 'Czy zasilanie awaryjne włącza się samo, czy trzeba je załączyć ręcznie? Które fazy, pomieszczenia i gniazdka będą zasilane przy braku prądu?')
  }
  if (magazyn && !magazyn.brak) {
    q.push('Czy w ofercie jest licznik energii (smart meter) albo przekładniki prądowe do falownika? Bez pomiaru przy liczniku operatora magazyn nie wie, kiedy ładować i rozładowywać.')
    q.push('Jaka jest gwarancja na magazyn: ile lat i ile cykli, i który limit skończy się pierwszy?')
  }
  q.push(...pytaniaOMarce(wynik?.falownik?.marka, 'falownika'))
  // Ta sama marka: pytania z falownika już to pokrywają, bez powtórek.
  if (magazyn && !magazyn.brak && !wspolnaMarka(wynik)) q.push(...pytaniaOMarce(wynik?.magazyn?.marka, 'magazynu'))
  return q
}
